import { Typography, message } from "antd";
import moment from "moment";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  DELETE_INVENTORY_MRR_REPORT,
  EXPORT_INVENTORY_MRR_REPORT,
  GET_INVENTORY_MRR_REPORT,
  VIEW_INVENTORY_MRR_REPORT,
} from "../../../apiServices/API_ENDPOINTS";
import { getData, postData } from "../../../apiServices/common";
import AppContent from "../../../common/AppContent";
import AppPageHeader from "../../../common/PageHeader";
import ListTable from "../../../common/ListTable";
import ExtraContent from "../../Configuration/CommonComponents/ExtraContent";
import FilterComponent from "../../Configuration/CommonComponents/FilterComponent";
import { cglFormatDate } from "utils/functions";

const InventoryMRR = () => {
  // States
  const [refresh, setRefresh] = useState(0);
  const [tableData, setTableData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showFilter, setShowFilter] = useState(false);
  // Fix round 1: last window actually queried, for the A3 empty-state hint.
  const [queryWindow, setQueryWindow] = useState({
    start: moment().subtract(6, "days"),
    end: moment(),
  });

  // Fix round 1: guard against setState after unmount (cold review finding 2, extended to
  // this page after the gate found it needed the same date-seeded VIEW-endpoint treatment
  // as pages 02/04/05/10 rather than the unfiltered GET list).
  const mountedRef = useRef(true);
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);
  // Final fix wave (C1): request-sequence ref — a fast manual Preview must never be
  // overwritten by a slower, stale mount request resolving afterwards.
  const reqIdRef = useRef(0);
  const lastFilterValuesRef = useRef(null);
  const [fetchError, setFetchError] = useState(null);

  // Antd
  const { Title } = Typography;

  const cutPath = (path) => {
    const updatedPath = path ? path.split("public/inventory_mrr/")[1] : "";
    return updatedPath;
  };

  const getAllMRR = useCallback(async () => {
    setLoading(true);
    const query = `${GET_INVENTORY_MRR_REPORT}`;
    const response = await getData(query);

    if (response?.status === 200) {
      setLoading(false);
      setTableData(response?.data?.data?.files);
    } else {
      setLoading(false);
      message.error("Error getting MRR data");
    }
  }, []);

  const exportMRR = async (filterValues) => {
    if (!Object.values(filterValues).some((val) => val === undefined)) {
      setLoading(true);
      const query = `${EXPORT_INVENTORY_MRR_REPORT}`;
      const { start_date, end_date, supplier_id } = filterValues || {};
      const bodyData = {
        start_date: start_date ? moment(start_date).format("YYYY-MM-DD") : null,
        end_date: end_date ? moment(end_date).format("YYYY-MM-DD") : null,
        supplier_id,
      };
      const response = await getData(
        query,
        false,
        bodyData,
        { "Content-Type": "blob" },
        "arraybuffer",
      );

      if (response && response?.data) {
        // Download the file
        const outputFilename = `${String(start_date)
          .split(" ")
          .join("-")}-${String(end_date).split(" ").join("-")}.xlsx`;

        // If you want to download file automatically using link attribute.
        const url = URL.createObjectURL(
          new Blob([response.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          }),
        );
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", outputFilename);
        document.body.appendChild(link);
        link.click();
        link.remove();

        setLoading(false);
        message.success("Inventry MRR Report Exported Successfully");
        refetch();
      } else {
        setLoading(false);
        message.error("Something went wrong");
      }
    }
  };

  const previewFunction = async (filterValues) => {
    // Final fix wave (C1): claim this request's sequence number before the await so a
    // later, faster request (e.g. the user's own manual Preview) can invalidate this one.
    const myReq = ++reqIdRef.current;
    lastFilterValuesRef.current = filterValues;
    setFetchError(null);
    setLoading(true);
    const { start_date, end_date, supplier_id } = filterValues || {};
    const bodyData = {
      start_date: start_date ? moment(start_date).format("YYYY-MM-DD") : null,
      end_date: end_date ? moment(end_date).format("YYYY-MM-DD") : null,
      supplier_id,
    };
    // Fix round 1: track the window actually queried for the A3 empty-state hint.
    if (start_date || end_date) {
      setQueryWindow((prev) => ({
        start: start_date ? moment(start_date) : prev.start,
        end: end_date ? moment(end_date) : prev.end,
      }));
    }

    const query = `${VIEW_INVENTORY_MRR_REPORT}`;
    const response = await postData(query, bodyData);

    // Fix round 1: mounted guard (cold review finding 2).
    // Final fix wave (C1): also drop this response if a newer request has since been fired
    // (stale-response race — see FINAL-REVIEW.md).
    if (!mountedRef.current || myReq !== reqIdRef.current) return;

    if (response?.code === 200) {
      setLoading(false);
      setTableData(response?.data);
    } else {
      setFetchError("Could not load this list.");
      setLoading(false);
      message.error("Error getting MRR data");
    }
  };

  // Effects
  // Fix round 1 (gate ruling): this page has REQUIRED Start/End Date pickers and a Preview
  // table, exactly like pages 02/04/05/10 — not a "generated file list" page. The old
  // getAllMRR()/GET_INVENTORY_MRR_REPORT mount call (previously wired here) returns the list
  // of previously generated export files (plus a supplier lookup list), not report rows, so
  // it never populated this page's real table. getAllMRR is left below exactly as it was
  // before the sweep (unused, dead); the mount effect now calls the page's own
  // previewFunction — the same function the "Preview" button calls via
  // VIEW_INVENTORY_MRR_REPORT — seeded with the last-7-days default, no supplier (matching
  // the initialValue defaults in
  // common/SearchForm/FilterFields/InventoryMRRFilterFields.js; confirmed via a direct API
  // call that the endpoint accepts a request with no supplier_id and returns 200).
  useEffect(() => {
    previewFunction({
      start_date: moment().subtract(6, "days").format("YYYY-MM-DD"),
      end_date: moment().format("YYYY-MM-DD"),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refetch = () => {
    setRefresh(refresh + 1);
  };

  const deleteSelectedMRRReport = async (name) => {
    setLoading(true);
    const updatedName = cutPath(name);
    const query = `${DELETE_INVENTORY_MRR_REPORT}`;
    const bodyData = {
      name: updatedName,
    };
    const response = await getData(query, false, bodyData);

    if (response && response?.data?.code === 200) {
      setLoading(false);
      message.success("Inventry MRR Report Deleted Successfully");
      refetch();
    } else {
      setLoading(false);
      message.error("Something went wrong");
    }
  };

  const openFile = (name) => {
    const URL = `${process.env.REACT_APP_BASE_URL}/storage/inventory_mrr/${name}`;
    window.open(URL, "_blank", "noreferrer");
  };

  const tableColumns = [
    {
      title: "Inventory MRR Information",
      children: [
        {
          title: "Serial No.",
          dataIndex: "serial",
          key: "serial",
          width: 150,
        },
        {
          title: "Date",
          dataIndex: "date",
          key: "date",
          width: 150,
          render: (row) => cglFormatDate(row),
        },
        {
          title: "Buying Office",
          dataIndex: "buying_office",
          key: "buying_office",
          width: 150,
        },
        {
          title: "Style",
          dataIndex: "style",
          key: "style",
          width: 150,
        },
        {
          title: "Color",
          dataIndex: "color",
          key: "color",
          width: 150,
        },
        {
          title: "Item Category",
          dataIndex: "item_type",
          key: "item_type",
          width: 150,
        },
        {
          title: "Item",
          dataIndex: "item",
          key: "item",
          width: 150,
        },
        {
          title: "Unit",
          dataIndex: "unit",
          key: "unit",
          width: 150,
        },
        {
          title: "Challan/Invoice No.",
          dataIndex: "challan_no",
          key: "challan_no",
          width: 150,
        },
        {
          title: "Quantity",
          dataIndex: "quantity",
          key: "quantity",
          width: 150,
        },
        {
          title: "Roll/PKG/Bag Qty",
          dataIndex: "shipment_qty",
          key: "shipment_qty",
          width: 150,
        },
      ],
    },
  ];

  const memoizedCustomFilter = useMemo(
    () => (
      <FilterComponent
        tableData={tableData}
        filterAPI={exportMRR}
        filterName={"inventory_mrr"}
        searchButtonText={"Export"}
        defaultOpen
        showFilter={showFilter}
        setShowFilter={setShowFilter}
        isPreview={true}
        previewFunction={previewFunction}
      />
    ),
    [showFilter],
  );

  return (
    <>
      <AppContent
        header={
          <AppPageHeader
            extra={
              <ExtraContent
                refresh={refresh}
                setRefresh={setRefresh}
                handleAddItemOpen={exportMRR}
                hideAddButton={true}
                showFilter={showFilter}
                setShowFilter={setShowFilter}
              />
            }
            title={<Title level={5}>Inventory MRR</Title>}
          />
        }
        content={
          <>
            <div className="table" style={{ marginTop: 10 }}>
              {memoizedCustomFilter}
              <ListTable
                loading={loading}
                error={fetchError}
                onRetry={() => previewFunction(lastFilterValuesRef.current)}
                dataSource={tableData}
                columns={tableColumns}
                pagination
                scroll={{ y: "max-content" }}
                bordered
                locale={{
                  emptyText: `No records between ${queryWindow.start.format(
                    "DD-MMM-YYYY",
                  )} and ${queryWindow.end.format("DD-MMM-YYYY")}. Widen the range above.`,
                }}
              />
            </div>
          </>
        }
      />
    </>
  );
};

export default InventoryMRR;
